import jsPDF from 'jspdf';
import {
  SigeProject, Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock,
  HAUPTABSCHNITTE, NotrufplanInhalt, LegendeEintrag, DEFAULT_PIKTO_KEYS, aufloesePikto,
  DEFAULT_PLAN_FARBEN, planHatZeitachse, planKleberandMm, planSchriftPt,
} from '../../components/sige-plan/types';
import type { EinweisungQrQuelle, PlanDarstellung, PlanVerantwortlicher } from '../../components/sige-plan/types';
import { imgFmt, fitLogoBox } from './letterhead';
import { NICHT_ANGEGEBEN } from './fehlendeAngabe';
import { MUSTER_LEGENDE, MUSTER_LMRA_FAKTOREN, MUSTER_LMRA_REGELN } from '../../components/sige-plan/musterVorlage';
import { GRUNDSATZ_ART_C, kurzform, legendeFuerDokumente } from '../mitgeltendeDokumente';

// ═══════════════════════════════════════════════════════════════
// SiGe-PLAN — PDF GENERATOR nach Muster-Layout (VERA-II)
// Matrix mit Abschnitts-Bändern, Monats-/Jahres-Zeitachse,
// Zuständigkeitsspalten + rechte Randspalte (Notrufplan, LMRA,
// Piktogramme, Legende, Plankopf)
//
// Schrift: EINE Grundschrift (`darstellung.schriftPt`, Vorgabe 10 pt) für
// die Matrix. Jede andere Schrift des Blatts und jeder textabhängige Abstand
// ist ein festes Vielfaches davon — der Regler vergrößert das ganze Blatt
// gleichmäßig. Bis Oktober 2026 stand die Matrix fest in 6 pt; auf dem
// Aushang am Container war das nur aus nächster Nähe lesbar.
// ═══════════════════════════════════════════════════════════════

const M = 8;            // Außenrand
const GAP = 4;          // Abstand Matrix ↔ Randspalte
// Blatthoehe: nach oben gekappt (Plotterrolle), nach unten offen — sie ergibt
// sich aus dem gezeichneten Inhalt (Messlauf), nicht aus einer Schaetzung.
// Die fruehere Untergrenze von 800 mm und die grosszuegige Randspalten-
// Schaetzung liessen am Blattende regelmaessig 40 cm und mehr Weisspapier.
const MAX_H = 5000;
// Luft zwischen Inhaltsende (Matrix oder Randspalte) und Fusszeile.
const LUFT_UNTEN = 6;
// Titelband ueber Matrix und Randspalte: 48-pt-Titel (Versalhoehe ≈ 12,2 mm)
// plus Unterlaengen und Luft bis zur Matrixkante. Der Titel ist schon gross
// genug und waechst mit der Grundschrift NICHT mit.
const TITLE_H = 20;
const TITEL_PT = 48;
// Untergrenze für frei eingebbare Plotterbreiten: Textspalten-Floor
// (MIN_COL_SCALE 0,35 auf FIXED_COLS_RAW 250 → 87,5 mm) + MIN_TIME_W (60)
// + Randspalte (130) + GAP (4) + 2×M (16) ≈ 297,5 mm — darunter würde die
// Zeitachse negativ. 300 mm mit Marge.
const MIN_PLOT_W = 300;

// Gewichte der Textspalten in mm bei Faktor 1. Die tatsaechliche Breite ist
// Gewicht × Faktor; der Faktor ergibt sich aus der Breite, die die Zeitachse
// uebrig laesst (siehe teileBreiteAuf). Am Musterplan bei 10 pt auf 610 und
// 914 mm abgestimmt: Massnahmen und Vorschriften bekommen den Platz, weil sie
// die Zeilenhoehe bestimmen; „Koordinierungsbedarf" ist so breit, dass das
// uebliche „alle Gewerke, Dritte" einzeilig bleibt (sonst wurde JEDE Zeile
// seinetwegen zweizeilig).
//
// Bis Oktober 2026 stand zwischen Vorschriften und Zeitachse eine Spalte
// „Hinweise" mit den BG-BAU-Bausteinen („B 213", meist „n.v."). Sie ist
// entfallen — die Codes stehen weiter am Schritt, gebraucht werden sie nur
// noch im Handbuch. Ihr Gewicht (12) ging an die Vorschriften (42 → 54), die
// jetzt auch die mitgeltenden Dokumente tragen; die Summe bleibt 250, damit
// MIN_PLOT_W und die Aufteilung schmaler Blaetter unveraendert gelten.
const COL = {
  arbeiten: 44,
  massnahme: 72,
  vorschrift: 54,
  koord: 26,
  zustaendig: 18,      // ×3
};
const FIXED_COLS_RAW = COL.arbeiten + COL.massnahme + COL.vorschrift + COL.koord + COL.zustaendig * 3;
const MIN_COL_SCALE = 0.35;

/**
 * Spalten der Matrix in Zeichenreihenfolge — Index in `colX`/`colW`. Benannt
 * statt fester Zahlen: beim Wegfall der Hinweis-Spalte waere sonst jeder
 * `colX[5]` stillschweigend eine Spalte weitergerutscht.
 */
const SP = { arbeiten: 0, massnahme: 1, vorschrift: 2, zeit: 3, koord: 4, erst: 5, kontr: 6, inst: 7 } as const;

// Zeitachse: Sie traegt nur Balken und Datum und stand frueher fuer den
// ganzen Rest der Matrixbreite — bei 914 mm Plotterbreite rund 480 mm fast
// leeres Raster, waehrend die Textspalten bei 6 pt umbrachen. Jetzt bekommt
// sie ein Soll je Monat, hoechstens einen festen Anteil der Matrix; alles
// Uebrige geht an die Textspalten und spart dort Zeilen.
const ZEIT_MM_PRO_MONAT = 10;
const ZEIT_ANTEIL_MAX = 0.25;
const MIN_TIME_W = 60;

/**
 * Zeitspalte ohne Zeitachse (`darstellung.zeitachse === false`: in der
 * Planungsphase liegt noch kein Bauzeitenplan vor). Sie bleibt stehen, damit
 * der Plan dem Muster folgt, traegt aber nur Spaltenkopf und Hinweis — die
 * Breite reicht, dass „Zeitraum gemeinsamer Nutzung / gegenseitiger
 * Gefährdung" bis 14 pt in KOPF_MAX_ZEILEN Zeilen ohne Wortbruch steht und
 * der volle Hinweis bei 10 pt zweizeilig ins Kopfband passt. Alles Uebrige
 * geht an die Textspalten. Exportiert fuer den Test.
 */
export const ZEIT_OHNE_ACHSE_W = 40;
/** Hinweis im Kopfband statt Jahr/Monat; die Kurzform, wenn der volle Wortlaut nicht lesbar hineinpasst. */
export const HINWEIS_OHNE_ZEITACHSE = 'Zeiträume folgen mit dem Bauzeitenplan (Fortschreibung)';
export const HINWEIS_OHNE_ZEITACHSE_KURZ = 'Zeiträume folgen mit dem Bauzeitenplan';

// Randspalte: ein Viertel der Blattbreite, mindestens 130 mm. Die Obergrenze
// liegt hoeher als frueher (170), damit die mitwachsende Schrift auf breiten
// Rollen nicht nur in die Laenge geht.
const SIDE_MIN = 130;
const SIDE_MAX = 230;

// Kantenlaenge des QR-Codes der Online-Einweisung in der Randspalte. 32 mm
// liest ein Handy aus Armlaenge; kleiner wird auf dem Plot grenzwertig.
const EINWEISUNG_QR_MM = 32;
// Kartenausschnitt: auf breiter Randspalte nicht beliebig hoch werden lassen.
const KARTE_MAX_W = 160;

// ── Schriftmasse ──────────────────────────────────────────────────
/** 1 pt in mm. */
const PT_MM = 25.4 / 72;
/**
 * Zeilenabstand als Vielfaches der Schriftgroesse. Gilt fuer JEDEN
 * mehrzeiligen text()-Aufruf (setLineHeightFactor) und fuer jede
 * Hoehenrechnung — Messung und Zeichnung koennen nicht auseinanderlaufen.
 */
const ZEILEN_FAKTOR = 1.2;
const zeilenMm = (fs: number) => fs * ZEILEN_FAKTOR * PT_MM;
/** Zeilenoberkante → erste Grundlinie. */
const oberMm = (fs: number) => fs * 0.85 * PT_MM;
/** Versalhoehe der Helvetica — zum senkrechten Zentrieren einzeiliger Texte. */
const versalMm = (fs: number) => fs * 0.72 * PT_MM;
/**
 * Grundschrift, fuer die alle festen Masse der Randspalte entworfen wurden.
 * Bei Grundschrift b wird jede dieser Groessen mit b / ENTWURF_PT skaliert.
 */
const ENTWURF_PT = 6;

/** Schrift der Fusszeile (7 pt bei 6 pt Grundschrift). */
const fussPt = (grundPt: number) => grundPt * 7 / ENTWURF_PT;
/**
 * Hoehe des Fussstreifens bei Grundschrift `grundPt`. Exportiert fuer den
 * Test: der Streifen muss genau am unteren Rand sitzen.
 */
export const fussHoeheMm = (grundPt: number): number => Math.max(6, fussPt(grundPt) * PT_MM * 1.7);

/**
 * Freier Rand oben und unten: der Kleberand des Plans, mindestens der
 * Aussenrand M. Gemessen ab der Blattkante — „20 mm Kleberand" heisst, die
 * ersten 20 mm sind leer, nicht 20 mm zusaetzlich zum Aussenrand. Links und
 * rechts bleibt M. Exportiert fuer den Test.
 */
export const randObenUntenMm = (d?: PlanDarstellung | null): number => Math.max(M, planKleberandMm(d));

/**
 * Stammdaten des Hauptprojekts fuer die Fusszeile. Der SiGe-Plan selbst kennt
 * nur seine Plan-Nr. (`SGP-…`) und den Baumassnahmen-Namen; die eigentliche
 * Projektnummer haelt das Hauptprojekt. Fehlt sie, entfaellt das Feld — eine
 * als Projektnummer beschriftete Plan-Nr. waere falsch.
 */
export interface SigePlanFusszeile {
  projektNummer?: string;
  projektName?: string;
}

/**
 * '#1f4e89' → [31, 78, 137]. Gibt `null` zurueck, wenn die Zeichenkette keine
 * Farbe ist — der Aufrufer bleibt dann bei seiner Vorgabe. Ein halb gelesener
 * Hexwert wuerde sonst als Schwarz drucken.
 */
export const hexZuRgb = (hex?: string | null): readonly [number, number, number] | null => {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
  if (!m) return null;
  const v = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)] as const;
};

const C = {
  dunkel: [33, 64, 95] as const,
  band: [80, 90, 100] as const,
  grid: [170, 170, 170] as const,
  gridLight: [215, 215, 215] as const,
  text: [20, 20, 20] as const,
  weiss: [255, 255, 255] as const,
  rot: [200, 30, 30] as const,
  // Zeitbalken: Blau statt Rot. Rot war auf diesem Blatt dreifach belegt —
  // Notrufplan-Kopf, Projektmarker der Karte, Brandschutz-Piktogramm; wenn
  // ausserdem JEDER Balken rot ist, hebt sich der kritische Schritt nicht mehr
  // ab. Ueberschreibbar ueber `project.darstellung` (Migration Nr. 62).
  //
  // Die Werte kommen aus DEFAULT_PLAN_FARBEN — derselben Quelle, aus der die
  // Oberflaeche ihre Farbfelder vorbelegt. Zwei getrennte Vorgaben waeren
  // auseinandergelaufen, und der Anwender saehe im Dialog eine andere Farbe als
  // auf dem Blatt.
  balken: hexZuRgb(DEFAULT_PLAN_FARBEN.balken)!,
  kritisch: hexZuRgb(DEFAULT_PLAN_FARBEN.balkenKritisch)!,
  gelb: [250, 200, 60] as const,
  blau: [25, 80, 160] as const,
  gruen: [40, 140, 70] as const,
  violett: [123, 63, 191] as const,
  zeitBg: [205, 225, 240] as const,
  hellgrau: [240, 240, 240] as const,
};

/**
 * Schriftfarbe auf einer Flaeche: Weiss auf dunklem, Schwarz auf hellem
 * Grund. Die Balkenfarbe ist frei waehlbar — feste weisse Schrift waere auf
 * einem gelben Balken unlesbar.
 */
const schriftAuf = (grund: readonly number[]): readonly number[] =>
  0.299 * grund[0] + 0.587 * grund[1] + 0.114 * grund[2] < 150 ? C.weiss : C.text;

const MONATE = ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const parseDate = (s?: string | null): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmtKurz = (d: Date) => `${d.getDate().toString().padStart(2, '0')}. ${MONATE[d.getMonth()]}`;
const fmtDatum = (s?: string) => {
  const d = parseDate(s);
  if (!d) return '';
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
};

const eindeutig = (werte: string[]) => Array.from(new Set(werte.map(w => w.trim()).filter(Boolean)));

/**
 * Kurzschreibweise der DGUV-Regelwerke fuer die Vorschriftenspalte:
 * „DGUV Vorschrift 38" → „DGUV V 38". Die Abkuerzungen sind die ueblichen
 * (V/R/I/G); alles andere bleibt wortgleich.
 */
export const kurzeVorschrift = (v: string): string =>
  v.trim()
    .replace(/\bDGUV[\s-]+Vorschrift\b/gi, 'DGUV V')
    .replace(/\bDGUV[\s-]+Regel\b/gi, 'DGUV R')
    .replace(/\bDGUV[\s-]+Information\b/gi, 'DGUV I')
    .replace(/\bDGUV[\s-]+Grundsatz\b/gi, 'DGUV G')
    .replace(/\s+/g, ' ');

/** Leerraum zusammenziehen — Zeilenumbrueche in Eingaben sind Zufall, keine Gliederung. */
const einzeilig = (t: unknown): string => String(t ?? '').replace(/\s+/g, ' ').trim();
/** Vergleichsschluessel fuer Dubletten: Gross-/Kleinschreibung und Leerraum zaehlen nicht. */
const schluessel = (t: string): string => einzeilig(t).toLowerCase();

/**
 * Mitgeltende Dokumente eines Schritts fuer Spalte 3: die Kurzform aus dem
 * Katalog (freie Eintraege bleiben wortgleich), ohne Dubletten. Zwei
 * Langnamen mit derselben Kurzform erscheinen einmal, und ein Dokument, das
 * in derselben Zelle schon als Vorschrift steht („BaustellenO"), nicht ein
 * zweites Mal darunter.
 */
export const dokumenteDerZeile = (
  s: Pick<Arbeitsschritt, 'mitgeltende_dokumente'>,
  vorschriften: string[] = [],
): string[] => {
  const gesehen = new Set(vorschriften.map(schluessel));
  const out: string[] = [];
  (s.mitgeltende_dokumente || []).forEach(name => {
    const lang = einzeilig(name);
    if (!lang) return;
    const kurz = einzeilig(kurzform(lang)) || lang;
    if (gesehen.has(schluessel(kurz))) return;
    gesehen.add(schluessel(kurz));
    out.push(kurz);
  });
  return out;
};

/**
 * Legende, wie sie auf das Blatt kommt:
 * - „n.v." faellt immer weg und „GM" (Gelbe Mappe), solange kein Schritt die
 *   Gelbe Mappe unter seinen Vorschriften fuehrt. Beide erklaerten die
 *   BG-BAU-Hinweisspalte, die es nicht mehr gibt; gespeicherte Legenden aus
 *   aelteren Plaenen tragen sie noch.
 * - Dahinter die Kurzformen der mitgeltenden Dokumente, die der Plan
 *   wirklich benutzt — ohne Kuerzel, die die Legende schon erklaert. So steht
 *   keine Kurzform in Spalte 3, die das Blatt nicht aufloest, ohne dass jemand
 *   die Legende von Hand nachpflegen muss.
 */
export const legendeFuerPlan = (gespeichert: LegendeEintrag[], schritte: Arbeitsschritt[]): LegendeEintrag[] => {
  const kuerzelSchluessel = (t?: string) => String(t ?? '').replace(/\s+/g, '').toLowerCase();
  const nutztGM = schritte.some(s => (s.vorschriften || []).some(v => /\bGM\b/.test(String(v ?? ''))));
  const basis = gespeichert.filter(le => {
    const kz = kuerzelSchluessel(le.kuerzel);
    if (/^n\.?v\.?$/.test(kz)) return false;
    return !(kz === 'gm' && !nutztGM);
  });
  const vorhanden = new Set(basis.map(le => kuerzelSchluessel(le.kuerzel)));
  const namen = eindeutig(schritte.flatMap(s => (s.mitgeltende_dokumente || []).map(einzeilig)));
  const dazu = legendeFuerDokumente(namen).filter(le => {
    const kz = kuerzelSchluessel(le.kuerzel);
    if (!kz || vorhanden.has(kz)) return false;
    vorhanden.add(kz);
    return true;
  });
  return [...basis, ...dazu];
};

/**
 * Kurze Eintraege (Vorschriften, mitgeltende Dokumente, Massnahmen) nebeneinander
 * statt je einer pro Zeile: so viele GANZE Eintraege je Zeile, wie in die
 * Breite passen. Ein Eintrag wird nur zerteilt, wenn er allein breiter als
 * die Spalte ist. Frueher stand jede Vorschrift auf eigener Zeile — in 39 von
 * 56 Musterzeilen bestimmte allein diese Spalte die Zeilenhoehe.
 */
export const packeEintraege = (
  eintraege: string[],
  breite: number,
  misst: (t: string) => number,
  teilt: (t: string, w: number) => string[],
  trenner: string,
): string[] => {
  const zeilen: string[] = [];
  let aktuell = '';
  eintraege.forEach(e => {
    const kandidat = aktuell ? aktuell + trenner + e : e;
    if (misst(kandidat) <= breite) { aktuell = kandidat; return; }
    if (aktuell) zeilen.push(aktuell);
    if (misst(e) <= breite) { aktuell = e; return; }
    // Alle Teile auf eigene Zeilen: teilte sich der Rest die Zeile mit dem
    // naechsten Eintrag, laese sich die Fortsetzung („Kapitel 2.8 · BaustellV")
    // wie eine eigene Vorschrift.
    zeilen.push(...teilt(e, breite));
    aktuell = '';
  });
  if (aktuell) zeilen.push(aktuell);
  return zeilen;
};

/**
 * Breitenaufteilung des Blatts. Invariante: Textspalten + Zeitachse ergeben
 * genau `matrixW` — sonst laeuft die Matrix unter die deckend gefuellte
 * Randspalte. Reicht die Breite nicht (schmale Blaetter), werden die
 * Textspalten bis MIN_COL_SCALE gestaucht und die Zeitachse bekommt den Rest
 * (MIN_PLOT_W garantiert, dass der Rest ≥ MIN_TIME_W bleibt).
 *
 * Der Rueckfall 610 greift nur noch, wenn ein Aufrufer die Firmenvorgabe
 * nicht einsetzt: die Oberflaeche reicht den Plan ueber `projektFuerDruck`
 * (services/sigePlanVorlagen.ts), das eine leere Breite durch die
 * Standardbreite der Firma ersetzt.
 *
 * `ohneZeitachse`: die Zeitspalte bekommt fest ZEIT_OHNE_ACHSE_W, die
 * Monatszahl zaehlt nicht. Die Invariante gilt unveraendert; MIN_PLOT_W laesst
 * auch dann mehr als MIN_COL_SCALE fuer die Textspalten.
 */
export const teileBreiteAuf = (
  plotBreite: number | null | undefined,
  anzahlMonate: number,
  opt?: { ohneZeitachse?: boolean },
) => {
  const W = Math.max(MIN_PLOT_W, plotBreite || 610);
  const sideW = Math.max(SIDE_MIN, Math.min(SIDE_MAX, W * 0.25));
  const matrixW = W - 2 * M - sideW - GAP;
  const zeitSoll = opt?.ohneZeitachse
    ? ZEIT_OHNE_ACHSE_W
    : Math.max(MIN_TIME_W, Math.min(anzahlMonate * ZEIT_MM_PRO_MONAT, matrixW * ZEIT_ANTEIL_MAX));
  const scale = Math.max(MIN_COL_SCALE, (matrixW - zeitSoll) / FIXED_COLS_RAW);
  const textW = FIXED_COLS_RAW * scale;
  const zeitW = matrixW - textW;
  const spalten = {
    arbeiten: COL.arbeiten * scale,
    massnahme: COL.massnahme * scale,
    vorschrift: COL.vorschrift * scale,
    koord: COL.koord * scale,
    zustaendig: COL.zustaendig * scale,
  };
  return { W, sideW, matrixW, zeitW, textW, scale, spalten };
};

/** Lage einer Datumsbeschriftung am Zeitbalken. `innen` = auf dem Balken. */
export interface DatumsPlatz { x: number; align: 'left' | 'right' | 'center'; innen: boolean; oben?: boolean }
export interface DatumsLayout { von?: DatumsPlatz; bis?: DatumsPlatz; beide?: DatumsPlatz }

/**
 * Wohin die Datumsangaben eines Balkens kommen. Bevorzugt wie im Muster:
 * Beginn links, Ende rechts neben dem Balken. Fehlt dort Platz (Balken am
 * Achsenrand — der Normalfall bei Schritten ueber die ganze Bauzeit), steht
 * das Datum auf dem Balken; reicht auch das nicht, beide zusammen
 * („01. Mrz – 30. Jun") neben oder auf dem Balken. Erst ganz zuletzt ueber
 * dem Balken, wie frueher. Rein geometrisch, damit testbar.
 */
export const legeDatumAb = (a: {
  links: number; rechts: number; x1: number; x2: number;
  wVon: number; wBis: number; wBeide: number; luft: number;
}): DatumsLayout => {
  const { links, rechts, x1, x2, wVon, wBis, wBeide, luft } = a;
  const platzL = x1 - links;
  const platzR = rechts - x2;
  const innen = x2 - x1 - 2 * luft;
  let von: DatumsPlatz | undefined = platzL >= wVon + 2 * luft ? { x: x1 - luft, align: 'right', innen: false } : undefined;
  let bis: DatumsPlatz | undefined = platzR >= wBis + 2 * luft ? { x: x2 + luft, align: 'left', innen: false } : undefined;
  if (!von && !bis) {
    if (innen >= wVon + wBis + 3 * luft) {
      von = { x: x1 + luft, align: 'left', innen: true };
      bis = { x: x2 - luft, align: 'right', innen: true };
    }
  } else if (!von) {
    if (innen >= wVon) von = { x: x1 + luft, align: 'left', innen: true };
  } else if (!bis) {
    if (innen >= wBis) bis = { x: x2 - luft, align: 'right', innen: true };
  }
  if (von && bis) return { von, bis };
  if (platzR >= wBeide + 2 * luft) return { beide: { x: x2 + luft, align: 'left', innen: false } };
  if (platzL >= wBeide + 2 * luft) return { beide: { x: x1 - luft, align: 'right', innen: false } };
  if (innen >= wBeide) return { beide: { x: (x1 + x2) / 2, align: 'center', innen: true } };
  const x = Math.max(links, Math.min(x1, rechts - wBeide));
  return { beide: { x, align: 'left', innen: false, oben: true } };
};

interface MonatsSpalte { jahr: number; monat: number; } // monat 0-11

/**
 * Umbrochener Zellinhalt mit seiner Schriftgroesse. `kursiv`: Zeilen, die
 * abgesetzt und kursiv UNTER `lines` stehen — die mitgeltenden Dokumente in
 * der Vorschriftenspalte. Beide Teile haben dieselbe Schriftgroesse, damit die
 * Zeilenrechnung eine bleibt.
 */
interface Zelle { lines: string[]; fs: number; kursiv?: string[] }

/**
 * Luft zwischen Vorschriften und mitgeltenden Dokumenten: ein Drittel Zeile.
 * Genug, dass die Dokumente als eigener Block lesbar sind; mehr verlaengerte
 * jede Zeile, die Dokumente traegt. Ohne einen der beiden Teile keine Luft.
 */
const kursivAbstand = (z: Zelle): number => (z.lines.length && z.kursiv?.length ? zeilenMm(z.fs) / 3 : 0);
/**
 * Hoehe des Zellinhalts. EINE Rechnung fuer Messung (Zeilenhoehe) und
 * Zeichnung (Lage des kursiven Blocks) — liefen sie auseinander, ragten die
 * Dokumente unten aus der Zeile.
 */
const zellHoehe = (z: Zelle): number => (z.lines.length + (z.kursiv?.length || 0)) * zeilenMm(z.fs) + kursivAbstand(z);

/**
 * Untergrenze, bis zu der eine einzelne Zelle kleiner gesetzt wird, damit ein
 * langes Wort („Elektrofachkraft", „Kampfmittelräumdienst") nicht mitten im
 * Wort umbricht: 45 % der Grundschrift, nie unter 5 pt. Reicht auch das nicht,
 * trennt jsPDF wie bisher zeichenweise.
 */
const MIN_WORT_FAKTOR = 0.45;
const MIN_WORT_PT = 5;
/** Kleinste Schrift eines Spaltenkopfs (wie vor dem Umbau). */
const KOPF_MIN_PT = 4;
/** Kleinste Schrift der Unterueberschriften Erstellung/Kontrolle/Instandhaltung (wie vor dem Umbau). */
const SUB_MIN_PT = 3.6;
/** Luft ueber und unter dem Hinweis im Kopfband (ohne Zeitachse). */
const HINWEIS_LUFT = 0.5;

/** Zeitbalken eines Schritts, x relativ zum Beginn der Zeitachse. */
interface BalkenLayout {
  x1: number;
  x2: number;
  vonLabel: string;
  bisLabel: string;
  beideLabel: string;
  lage: DatumsLayout;
}

interface ZeileLayout {
  schritt: Arbeitsschritt;
  hoehe: number;
  arbeiten: Zelle;
  massnahme: Zelle;
  /** Vorschriften, darunter kursiv die mitgeltenden Dokumente (`kursiv`). */
  vorschrift: Zelle;
  koord: Zelle;
  /**
   * Erstellung, Kontrolle und Instandhaltung liegen bei derselben Stelle —
   * dann EINE Zelle ueber alle drei Spalten (`erst`), statt dreimal
   * denselben Namen umbrechen zu lassen. Im Muster trifft das 44 von 56 Zeilen.
   */
  zustVerbunden: boolean;
  erst: Zelle;
  kontr: Zelle;
  inst: Zelle;
  balken: BalkenLayout | null;
}

interface SektionLayout {
  titel: string;
  zeilen: ZeileLayout[];
}

interface PlanEingabe {
  project: SigeProject;
  arbeitsschritte: Arbeitsschritt[];
  gefaehrdungen: Record<string, Gefaehrdung[]>;
  massnahmen: Record<string, Schutzmassnahme[]>;
  bloecke: SigeBlock[];
  /** key → PNG-DataURL (echte ISO-Zeichen) */
  piktoBilder: Record<string, string>;
  /**
   * QR-Code der Online-Einweisung (juengste Einweisung mit aktivem Link).
   * null/undefined = kein aktiver Link: der Plan ist dann zeichengleich mit
   * einem Plan ohne diesen Parameter — kein Platzhalter, keine Leerbox.
   */
  einweisungQr?: EinweisungQrQuelle | null;
  /**
   * Projektnummer und -bezeichnung des Hauptprojekts fuer die Fusszeile.
   * Ohne Angabe traegt die Fusszeile nur Baumassnahme und Koordinator.
   */
  fusszeile?: SigePlanFusszeile | null;
}

/** Spaltentitel je Einzelspalte; die drei Zustaendigkeitsspalten tragen einen Gruppentitel. */
const KOPF_TITEL: { sp: number; text: string }[] = [
  { sp: SP.arbeiten, text: 'Arbeiten bzw. Leistungen von denen Gefährdungen für andere Gewerke ausgehen' },
  { sp: SP.massnahme, text: 'Schutzeinrichtung bzw. Schutzmaßnahme' },
  { sp: SP.vorschrift, text: 'Arbeitsvorschriften und mitgeltende Dokumente' },
  { sp: SP.zeit, text: 'Zeitraum gemeinsamer Nutzung / gegenseitiger Gefährdung' },
  { sp: SP.koord, text: 'Koordinierungsbedarf aufgrund gemeinsamer Nutzung oder gegenseitiger Gefährdung' },
];
const KOPF_ZUSTAENDIG = 'Zuständigkeit für die Schutzeinrichtungen bzw. Schutzmaßnahmen';
const KOPF_MAX_ZEILEN = 5;

/**
 * Zeichnet den Plan auf ein Blatt der Hoehe `blattHoehe`. `null` = Messlauf
 * auf einem uebergrossen Blatt (MAX_H): erst nach dem Zeichnen ist bekannt,
 * wie tief die dynamisch gesetzte Randspalte wirklich reicht. `unten` ist die
 * tiefste Inhaltskante (Matrix oder Randspalte) ohne Fusszeile.
 *
 * Beide Laeufe zeichnen exakt dasselbe, Bilder eingeschlossen — ein Lauf mit
 * Sonderpfaden koennte anders umbrechen, und dann stimmt die Hoehe nicht.
 */
const zeichnePlan = (e: PlanEingabe, blattHoehe: number | null): { doc: jsPDF; unten: number } => {
  const { project, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, piktoBilder, einweisungQr, fusszeile } = e;

  // ── Schrift ────────────────────────────────────────────────────
  const b = planSchriftPt(project.darstellung);     // Grundschrift der Matrix
  const k = b / ENTWURF_PT;                          // Faktor fuer alle Entwurfsmasse
  const p = (pt: number) => pt * k;                  // Entwurfs-pt → Druck-pt
  const pad = Math.max(1.2, 0.15 * b);               // Zellinnenabstand
  const lineH = zeilenMm(b);
  const balkenH = Math.max(2.6, 0.42 * b);
  const datumPt = 0.8 * b;
  const datumLuft = 0.8 * k;          // Abstand Datum ↔ Balkenende
  const datumHoch = 0.6 * k;          // Grundlinie über dem Balken (Notfalllage)
  const minWortPt = Math.max(MIN_WORT_PT, MIN_WORT_FAKTOR * b);
  const minRowH = Math.max(7, 2 * pad + lineH, balkenH + 2 * pad);

  // Freier Rand oben und unten (Kleberand), siehe randObenUntenMm.
  const randOU = randObenUntenMm(project.darstellung);

  // ── Zeitachse bestimmen ────────────────────────────────────────
  // Ohne Zeitachse (Bauzeitenplan liegt noch nicht vor) bleibt die
  // Monatsliste leer: kein Raster, keine Balken, keine Daten — auch wenn
  // Schritte schon Daten tragen. Die Zeitspalte zeigt dann den Hinweis.
  const mitZeitachse = planHatZeitachse(project.darstellung);
  const datumKandidaten: Date[] = [];
  const pv = parseDate(project.zeitraum_von);
  const pb = parseDate(project.zeitraum_bis);
  if (pv) datumKandidaten.push(pv);
  if (pb) datumKandidaten.push(pb);
  arbeitsschritte.forEach(s => {
    const v = parseDate(s.zeitraum_von); if (v) datumKandidaten.push(v);
    const bis = parseDate(s.zeitraum_bis); if (bis) datumKandidaten.push(bis);
  });
  let start: Date, ende: Date;
  if (datumKandidaten.length) {
    start = new Date(Math.min(...datumKandidaten.map(d => d.getTime())));
    ende = new Date(Math.max(...datumKandidaten.map(d => d.getTime())));
  } else {
    start = new Date();
    ende = new Date(start.getFullYear() + 1, start.getMonth(), 1);
  }
  const monate: MonatsSpalte[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endeMonat = new Date(ende.getFullYear(), ende.getMonth(), 1);
  while (mitZeitachse && cursor <= endeMonat && monate.length < 60) {
    monate.push({ jahr: cursor.getFullYear(), monat: cursor.getMonth() });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  // ── Breiten ────────────────────────────────────────────────────
  const { W, sideW: SIDE_W, matrixW: MATRIX_W, zeitW: TIME_W, spalten: CW } =
    teileBreiteAuf(project.plot_breite_mm, monate.length, { ohneZeitachse: !mitZeitachse });

  // Ohne Monate kein Teilen durch 0 und kein monate[0]. Die Achse wird dann
  // nie befragt (balkenLayout liefert vorher null).
  const monatW = monate.length ? TIME_W / monate.length : 0;
  const ersterMonat = monate[0] ?? { jahr: start.getFullYear(), monat: start.getMonth() };
  const letzterMonat = monate[monate.length - 1] ?? ersterMonat;
  const zeitStart = new Date(ersterMonat.jahr, ersterMonat.monat, 1);
  const zeitEnde = new Date(letzterMonat.jahr, letzterMonat.monat + 1, 0);
  const zeitSpanne = zeitEnde.getTime() - zeitStart.getTime();
  const xFuerDatum = (d: Date): number => {
    const t = Math.max(0, Math.min(1, (d.getTime() - zeitStart.getTime()) / zeitSpanne));
    return t * TIME_W;
  };

  // Mess-Dokument (Fontmetrik) — endgültige Höhe erst nach Layout bekannt
  const probe = new jsPDF({ unit: 'mm', orientation: 'portrait', format: [W, 1000] });
  probe.setFont('helvetica', 'normal');

  // ── Zeilen-Layout messen ───────────────────────────────────────
  // Der Umbruch muss mit demselben Schriftschnitt gemessen werden, mit dem später
  // gezeichnet wird — kritische Schritte rendern die Arbeiten-Spalte fett und
  // liefen sonst über die Spaltenkante hinaus.
  // Ist ein einzelnes Wort breiter als die Spalte, wird nur diese Zelle so
  // weit kleiner gesetzt, dass es passt (bis minWortPt) — sonst brach
  // „Elektrofachkraft" schon bei 10 pt als „Elektrofachkr|aft" um. Gemessen
  // im Schnitt, in dem das Wort gezeichnet wird: kursiv laeuft breiter als
  // normal, fett noch breiter.
  type Schnitt = 'normal' | 'bold' | 'italic';
  const zellGroesse = (woerter: string[], nutz: number, schnitt: Schnitt): number => {
    probe.setFont('helvetica', schnitt);
    probe.setFontSize(b);
    const laengstes = Math.max(0, ...woerter.map(w => probe.getTextWidth(w)));
    const fs = laengstes > nutz ? Math.max(minWortPt, Math.floor(b * nutz / laengstes * 10) / 10) : b;
    probe.setFontSize(fs);
    return fs;
  };
  // jsPDF liefert bei winziger Breite leere Zeilen (['', '', 'B ', …]) — die
  // haetten die Zeile ohne Inhalt hochgetrieben.
  const ohneLeere = (lines: string[]) => lines.filter(l => l.trim() !== '');
  const wrap = (text: string, breite: number, bold = false): Zelle => {
    const nutz = Math.max(0.5, breite - 2 * pad);
    const absaetze = text.split('\n');
    const fs = zellGroesse(absaetze.flatMap(t => t.split(/\s+/)), nutz, bold ? 'bold' : 'normal');
    return { lines: ohneLeere(absaetze.flatMap(t => probe.splitTextToSize(t, nutz) as string[])), fs };
  };
  const woerterVon = (eintraege: string[]) => eintraege.flatMap(e => e.split(/\s+/));
  // Packt in Schnitt und Groesse, in denen spaeter gezeichnet wird.
  const packeIn = (eintraege: string[], nutz: number, fs: number, schnitt: Schnitt, trenner: string): string[] => {
    if (!eintraege.length) return [];
    probe.setFont('helvetica', schnitt);
    probe.setFontSize(fs);
    return ohneLeere(packeEintraege(
      eintraege, nutz,
      t => probe.getTextWidth(t),
      (t, w) => probe.splitTextToSize(t, w) as string[],
      trenner,
    ));
  };
  const packe = (eintraege: string[], breite: number, trenner: string): Zelle => {
    if (!eintraege.length) return { lines: [], fs: b };
    const nutz = Math.max(0.5, breite - 2 * pad);
    const fs = zellGroesse(woerterVon(eintraege), nutz, 'normal');
    return { lines: packeIn(eintraege, nutz, fs, 'normal', trenner), fs };
  };
  // Spalte 3: oben die Vorschriften, abgesetzt darunter kursiv die
  // mitgeltenden Dokumente. EINE Schriftgroesse fuer die ganze Zelle — die
  // kleinere, die jeder Teil in seinem Schnitt fuer sein laengstes Wort
  // braucht; zwei Groessen in einer Zelle saehen nach Versehen aus.
  const packeVorschrift = (vorschriften: string[], dokumente: string[], breite: number): Zelle => {
    if (!vorschriften.length && !dokumente.length) return { lines: [], fs: b };
    const nutz = Math.max(0.5, breite - 2 * pad);
    const fs = Math.min(
      zellGroesse(woerterVon(vorschriften), nutz, 'normal'),
      zellGroesse(woerterVon(dokumente), nutz, 'italic'),
    );
    return {
      lines: packeIn(vorschriften, nutz, fs, 'normal', ' · '),
      fs,
      kursiv: packeIn(dokumente, nutz, fs, 'italic', ' · '),
    };
  };
  // Lage der Datumsangaben schon beim Messen: muss das Datum ueber den Balken
  // ausweichen, braucht die Zeile dort Platz — sonst ragte es in die Zeile
  // darueber.
  const balkenLayout = (s: Arbeitsschritt): BalkenLayout | null => {
    if (!mitZeitachse) return null;
    const von = parseDate(s.zeitraum_von);
    const bis = parseDate(s.zeitraum_bis);
    if (!von || !bis || bis < von) return null;
    const x1 = xFuerDatum(von);
    const x2 = Math.max(xFuerDatum(bis), x1 + 0.8);
    probe.setFont('helvetica', 'normal');
    probe.setFontSize(datumPt);
    const vonLabel = fmtKurz(von);
    const bisLabel = fmtKurz(bis);
    const beideLabel = `${vonLabel} – ${bisLabel}`;
    const lage = legeDatumAb({
      links: 0, rechts: TIME_W, x1, x2,
      wVon: probe.getTextWidth(vonLabel), wBis: probe.getTextWidth(bisLabel), wBeide: probe.getTextWidth(beideLabel),
      luft: datumLuft,
    });
    return { x1, x2, vonLabel, bisLabel, beideLabel, lage };
  };
  const leer: Zelle = { lines: [], fs: b };

  const layoutZeile = (s: Arbeitsschritt): ZeileLayout => {
    // Maßnahmen: direkt am Schritt; Fallback = verschachtelte Gefährdungs-Maßnahmen (Altdaten)
    let mass = s.schutzmassnahmen || [];
    if (mass.length === 0) {
      const gefs = gefaehrdungen[s.id] || [];
      mass = gefs.flatMap(g => (massnahmen[g.id] || []).map(m => m.beschreibung));
    }
    const arbeitenText = s.bezeichnung + (s.gewerk ? `\n(${s.gewerk})` : '');
    const arbeiten = wrap(arbeitenText, CW.arbeiten, !!s.kritisch);
    // Ein Zeilenumbruch innerhalb einer Massnahme ist Eingabezufall, keine
    // Gliederung — er wuerde das Packen sprengen.
    const massnahme = packe(eindeutig(mass.map(m => String(m ?? '').replace(/\s*\n\s*/g, ' '))).map(m => '• ' + m), CW.massnahme, '   ');
    // BG-BAU-Codes (`hinweis_codes`/`hinweis_code`) druckt der Plan nicht
    // mehr — siehe COL.
    const vorschriften = eindeutig((s.vorschriften || []).map(kurzeVorschrift));
    const vorschrift = packeVorschrift(vorschriften, dokumenteDerZeile(s, vorschriften), CW.vorschrift);
    const koord = wrap(s.koordinierungsbedarf || '', CW.koord);
    const erstText = (s.zustaendig_erstellung || '').trim();
    const kontrText = (s.zustaendig_kontrolle || '').trim();
    const instText = (s.zustaendig_instandhaltung || '').trim();
    const zustVerbunden = !!erstText && erstText === kontrText && kontrText === instText;
    const erst = wrap(erstText, zustVerbunden ? CW.zustaendig * 3 : CW.zustaendig);
    const kontr = zustVerbunden ? leer : wrap(kontrText, CW.zustaendig);
    const inst = zustVerbunden ? leer : wrap(instText, CW.zustaendig);
    const textH = Math.max(lineH, ...[arbeiten, massnahme, vorschrift, koord, erst, kontr, inst].map(zellHoehe));
    const balken = balkenLayout(s);
    const minH = balken?.lage.beide?.oben
      ? Math.max(minRowH, balkenH + 2 * (datumHoch + versalMm(datumPt) + 0.4))
      : minRowH;
    return {
      schritt: s,
      hoehe: Math.max(minH, textH + 2 * pad),
      arbeiten, massnahme, vorschrift, koord,
      zustVerbunden, erst, kontr, inst, balken,
    };
  };

  // Gruppierung nach Hauptabschnitt in Muster-Reihenfolge
  const reihenfolge = [...HAUPTABSCHNITTE] as string[];
  const sektionMap = new Map<string, Arbeitsschritt[]>();
  arbeitsschritte.forEach(s => {
    const key = s.hauptabschnitt || 'Weitere Arbeiten';
    if (!sektionMap.has(key)) sektionMap.set(key, []);
    sektionMap.get(key)!.push(s);
  });
  const sektionen: SektionLayout[] = Array.from(sektionMap.entries())
    .sort((a, bb) => {
      const ia = reihenfolge.indexOf(a[0]);
      const ib = reihenfolge.indexOf(bb[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    })
    .map(([titel, liste]) => ({ titel, zeilen: liste.map(layoutZeile) }));

  // ── Kopf messen ────────────────────────────────────────────────
  // Breiten je Spalte, ueber SP adressiert — die Reihenfolge der Zuweisungen
  // ist egal, der Index allein bestimmt die Lage.
  const colW: number[] = [];
  colW[SP.arbeiten] = CW.arbeiten;
  colW[SP.massnahme] = CW.massnahme;
  colW[SP.vorschrift] = CW.vorschrift;
  colW[SP.zeit] = TIME_W;
  colW[SP.koord] = CW.koord;
  colW[SP.erst] = CW.zustaendig;
  colW[SP.kontr] = CW.zustaendig;
  colW[SP.inst] = CW.zustaendig;
  const kopfPt = 0.97 * b;
  // Groesste Stufe, bei der die Ueberschrift in hoechstens `maxZeilen` Zeilen
  // passt, ohne ein Wort zu zerteilen („Arbeitsvorschrif|ten"). Passt keine, die kleinste
  // Stufe mit ALLEN Zeilen — das Band waechst mit; abgeschnitten wird nie
  // (sonst fehlte auf schmalen Blaettern „… Gefährdung").
  const kopfLayout = (text: string, w: number, maxZeilen = KOPF_MAX_ZEILEN): Zelle => {
    probe.setFont('helvetica', 'bold');
    const breite = Math.max(1, w - 2);
    const woerter = text.split(/\s+/);
    const stufen = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4].map(f => f * kopfPt).filter(fs => fs > KOPF_MIN_PT);
    stufen.push(KOPF_MIN_PT);
    for (const fs of stufen) {
      probe.setFontSize(fs);
      if (woerter.some(wort => probe.getTextWidth(wort) > breite)) continue;
      const lines = probe.splitTextToSize(text, breite) as string[];
      if (lines.length <= maxZeilen) return { lines, fs };
    }
    probe.setFontSize(KOPF_MIN_PT);
    return { lines: ohneLeere(probe.splitTextToSize(text, breite) as string[]), fs: KOPF_MIN_PT };
  };
  const kopfEinzel = KOPF_TITEL.map(t => ({ sp: t.sp, ...kopfLayout(t.text, colW[t.sp]) }));
  const kopfGruppe = kopfLayout(KOPF_ZUSTAENDIG, CW.zustaendig * 3, 3);
  // Unterueberschriften: bei gestauchten Spalten Schrift absenken statt über die Spaltenkante zu laufen
  const SUB_LABELS = ['Erstellung', 'Kontrolle', 'Instandhaltung'];
  let subPt = 0.9 * b;
  probe.setFont('helvetica', 'bold');
  probe.setFontSize(subPt);
  while (subPt > SUB_MIN_PT && SUB_LABELS.some(t => probe.getTextWidth(t) > CW.zustaendig - 1)) {
    subPt = Math.max(SUB_MIN_PT, subPt - 0.2);
    probe.setFontSize(subPt);
  }
  const subH = zeilenMm(subPt) + 0.6;
  // Hoehe des Spaltentitel-Bands: die laengste Ueberschrift muss vollstaendig
  // hinein — bei zu knappem Band hingen die letzten Zeilen unter dem grauen Band.
  const HEAD_TITEL_H = Math.max(
    ...kopfEinzel.map(l => 2 * pad + l.lines.length * zeilenMm(l.fs)),
    2 * pad + kopfGruppe.lines.length * zeilenMm(kopfGruppe.fs) + subH,
  );
  const HEAD_JAHR_H = Math.max(4.5, zeilenMm(b) + 1);
  const monatPt = 0.83 * b;
  const HEAD_MONAT_H = Math.max(4.5, zeilenMm(monatPt) + 1);
  const secPt = 1.08 * b;
  const SECTION_H = Math.max(5.5, zeilenMm(secPt) + 1.6);

  // Ohne Zeitachse steht im Band von Jahr und Monat der Hinweis, kursiv. Der
  // volle Wortlaut in der groessten Stufe ab der Monatsschrift, die ohne
  // Wortbruch ins Band passt — bis 80 % davon, darunter waere er auf dem
  // Aushang kaum zu lesen; sonst die Kurzform bis KOPF_MIN_PT. Passt auch die
  // nicht, waechst das Band: abgeschnitten wird nie (wie bei kopfLayout).
  const hinweisLayout = (w: number, bandH: number): Zelle => {
    const breite = Math.max(1, w - 2 * pad);
    const hoch = bandH - 2 * HINWEIS_LUFT;
    probe.setFont('helvetica', 'italic');
    const stufen = (untergrenze: number) =>
      [1, 0.9, 0.8, 0.7, 0.6, 0.5].map(f => f * monatPt).filter(fs => fs >= untergrenze);
    const versuche = (text: string, liste: number[]): Zelle | null => {
      for (const fs of liste) {
        probe.setFontSize(fs);
        if (text.split(/\s+/).some(wort => probe.getTextWidth(wort) > breite)) continue;
        const lines = ohneLeere(probe.splitTextToSize(text, breite) as string[]);
        if (lines.length * zeilenMm(fs) <= hoch) return { lines, fs };
      }
      return null;
    };
    const voll = versuche(HINWEIS_OHNE_ZEITACHSE, stufen(0.8 * monatPt));
    if (voll) return voll;
    const kurz = versuche(HINWEIS_OHNE_ZEITACHSE_KURZ, stufen(KOPF_MIN_PT));
    if (kurz) return kurz;
    probe.setFontSize(KOPF_MIN_PT);
    return { lines: ohneLeere(probe.splitTextToSize(HINWEIS_OHNE_ZEITACHSE_KURZ, breite) as string[]), fs: KOPF_MIN_PT };
  };
  const hinweis = mitZeitachse ? null : hinweisLayout(TIME_W, HEAD_JAHR_H + HEAD_MONAT_H);
  // Mehrhoehe des Bands fuer den Hinweis; mit Zeitachse immer 0.
  const hinweisZusatz = hinweis
    ? Math.max(0, zellHoehe(hinweis) + 2 * HINWEIS_LUFT - (HEAD_JAHR_H + HEAD_MONAT_H))
    : 0;

  const headerH = HEAD_TITEL_H + HEAD_JAHR_H + HEAD_MONAT_H + hinweisZusatz;
  const matrixInhaltH = sektionen.reduce((acc, sek) =>
    acc + SECTION_H + sek.zeilen.reduce((a, z) => a + z.hoehe, 0), 0);
  // Randspalte: Bloecke und ihre Inhalte (gezeichnet wird dynamisch)
  const notruf = bloecke.find(bl => bl.typ === 'notrufplan');
  const lmra = bloecke.find(bl => bl.typ === 'risk_assessment');
  const piktoBlock = bloecke.find(bl => bl.typ === 'piktogrammfeld');
  const legendeBlock = bloecke.find(bl => bl.typ === 'legende');
  // Ohne gespeicherten Block die Mustervorlage — die Legendenbox steht also
  // auf JEDEM Blatt. Deshalb gehoeren die Kurzformen der Dokumente und der
  // Grundsatz-Satz dort hinein (siehe Abschnitt 4 der Randspalte).
  const legendeEintraege: LegendeEintrag[] = legendeFuerPlan(
    legendeBlock?.inhalt?.eintraege?.length ? legendeBlock.inhalt.eintraege : MUSTER_LEGENDE,
    arbeitsschritte,
  );
  const piktoKeys: string[] = piktoBlock?.inhalt?.icons?.length
    ? piktoBlock.inhalt.icons
    : DEFAULT_PIKTO_KEYS;
  const lmraZeilen: string[] = lmra?.inhalt?.zeilen?.length
    ? lmra.inhalt.zeilen
    : [...MUSTER_LMRA_FAKTOREN, '', ...MUSTER_LMRA_REGELN];

  // Blatthoehe: im Messlauf ein uebergrosses Blatt, damit nichts abgeschnitten
  // wird; in der Reinzeichnung die gemessene Hoehe (siehe generateSigePlanPdf).
  const H = blattHoehe ?? MAX_H;

  // ── Dokument ───────────────────────────────────────────────────
  // jsPDF vertauscht bei 'portrait' stillschweigend Breite und Hoehe, sobald
  // W > H — bei einem kurzen Plan auf breitem Blatt stuende der Inhalt dann
  // auf einem hochkant gedrehten Blatt. Die fruehere Untergrenze von 800 mm
  // verdeckte das; jetzt richtet sich die Orientierung nach dem Seitenmass.
  // `compress`: verlustfrei (Flate) — ohne liegen Inhalt und Bilder roh im PDF,
  // zwölf Piktogramme aus 2048-px-PNGs machten so über 30 MB.
  const doc = new jsPDF({ unit: 'mm', orientation: W > H ? 'landscape' : 'portrait', format: [W, H], compress: true });
  doc.setFont('helvetica', 'normal');
  doc.setLineHeightFactor(ZEILEN_FAKTOR);

  const setFill = (c: readonly number[]) => doc.setFillColor(c[0], c[1], c[2]);
  const setDraw = (c: readonly number[]) => doc.setDrawColor(c[0], c[1], c[2]);
  const setText = (c: readonly number[]) => doc.setTextColor(c[0], c[1], c[2]);

  // Farben dieses Plans: Vorgabe, sofern `darstellung` nichts anderes sagt.
  // Unlesbare Werte fallen still auf die Vorgabe zurueck (hexZuRgb → null) —
  // ein halb gelesener Hexwert wuerde sonst schwarz drucken.
  const dst = project.darstellung || {};
  const F = {
    balken: hexZuRgb(dst.balken) ?? C.balken,
    kritisch: hexZuRgb(dst.balkenKritisch) ?? C.kritisch,
    kopf: hexZuRgb(dst.kopfBand) ?? C.dunkel,
  };

  // Plankopf- und Fusszeilenfelder sind frei befüllt (Firmennamen, Adressen,
  // E-Mails) und liefen ohne Begrenzung über die Box- und die Blattkante hinaus.
  // fit  = auf n Zeilen kürzen, fitEin = einzeilig, notfalls Schrift absenken.
  const fit = (t: string, w: number, n = 1): string[] =>
    (doc.splitTextToSize(t || '', Math.max(1, w)) as string[]).slice(0, n);
  const fitEin = (t: string, w: number, size: number, min = 4): string => {
    let fs = size;
    doc.setFontSize(fs);
    while (fs > min && doc.getTextWidth(t) > w) { fs = Math.max(min, fs - 0.2); doc.setFontSize(fs); }
    return doc.getTextWidth(t) > w ? (fit(t, w, 1)[0] || '') : t;
  };

  // Titel oben links (kursiv wie Muster), 48 pt — auf dem Plot muss er aus
  // einigen Metern lesbar sein. Auf schmalen Blaettern (MIN_PLOT_W) laeuft er
  // sonst unter die deckend gefuellte Randspalte; dann Schrift absenken.
  {
    const titel = 'Sicherheits- und Gesundheitsschutzplan';
    doc.setFont('helvetica', 'bolditalic');
    let fs = TITEL_PT;
    doc.setFontSize(fs);
    while (fs > 16 && doc.getTextWidth(titel) > W - 2 * M) { fs -= 1; doc.setFontSize(fs); }
    setText(C.text);
    doc.text(titel, M, randOU + 12.5);
    doc.setFont('helvetica', 'normal');
  }

  // ── Matrix-Kopf ────────────────────────────────────────────────
  const mx = M;                       // Matrix-Ursprung x
  const my = randOU + TITLE_H;        // Matrix-Ursprung y
  const colX: number[] = [];          // linke Kanten aller Spalten
  {
    let x = mx;
    colW.forEach(w => { colX.push(x); x += w; });
    colX.push(x); // rechte Kante
  }

  // Kopfzeile 1: Spaltentitel
  setFill(C.hellgrau);
  doc.rect(mx, my, MATRIX_W, HEAD_TITEL_H, 'F');
  setDraw(C.grid); doc.setLineWidth(0.2);
  doc.rect(mx, my, MATRIX_W, headerH);

  setText(C.text);
  doc.setFont('helvetica', 'bold');
  kopfEinzel.forEach(l => {
    const x = colX[l.sp];
    const w = colW[l.sp];
    doc.setFontSize(l.fs);
    doc.text(l.lines, x + w / 2, my + pad + oberMm(l.fs), { align: 'center' });
    doc.line(x, my, x, my + headerH);
  });
  // Zuständigkeit als Gruppe über 3 Spalten
  {
    const x = colX[SP.erst];
    const w = CW.zustaendig * 3;
    doc.setFontSize(kopfGruppe.fs);
    doc.text(kopfGruppe.lines, x + w / 2, my + pad + oberMm(kopfGruppe.fs), { align: 'center' });
    doc.setFontSize(subPt);
    const subY = my + HEAD_TITEL_H - pad - 0.25 * subPt * PT_MM;
    const subSpalten = [SP.erst, SP.kontr, SP.inst];
    SUB_LABELS.forEach((t, i) => doc.text(t, colX[subSpalten[i]] + CW.zustaendig / 2, subY, { align: 'center' }));
    const subTop = my + HEAD_TITEL_H - pad - subH;
    doc.line(x, my, x, my + headerH);
    doc.line(colX[SP.kontr], subTop, colX[SP.kontr], my + headerH);
    doc.line(colX[SP.inst], subTop, colX[SP.inst], my + headerH);
  }

  // Kopfzeile 2+3: Jahre + Monate (nur über Zeitachse). Ohne Zeitachse ist
  // `monate` leer — die Schleifen unten zeichnen nichts, das Band traegt
  // ueber beide Zeilen den Hinweis.
  const tx = colX[SP.zeit];
  const jahrY = my + HEAD_TITEL_H;
  const monatY = jahrY + HEAD_JAHR_H;
  setFill(C.zeitBg);
  doc.rect(tx, jahrY, TIME_W, HEAD_JAHR_H + HEAD_MONAT_H + hinweisZusatz, 'F');
  if (hinweis) {
    const bandH = HEAD_JAHR_H + HEAD_MONAT_H + hinweisZusatz;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(hinweis.fs);
    setText(C.text);
    doc.text(hinweis.lines, tx + TIME_W / 2, jahrY + (bandH - zellHoehe(hinweis)) / 2 + oberMm(hinweis.fs), { align: 'center' });
  }

  doc.setFont('helvetica', 'bold');
  setText(C.text);
  // Jahresgruppen
  let gi = 0;
  while (gi < monate.length) {
    const jahr = monate[gi].jahr;
    let gj = gi;
    while (gj < monate.length && monate[gj].jahr === jahr) gj++;
    const x1 = tx + gi * monatW;
    const x2 = tx + gj * monatW;
    // Breiten-Guard wie bei den Monaten: passt die vierstellige Jahreszahl nicht
    // in die Gruppe, zweistellig versuchen, sonst weglassen.
    doc.setFontSize(b);
    const platz = (x2 - x1) - 1;
    let jl = String(jahr);
    if (doc.getTextWidth(jl) > platz) jl = jl.slice(2);
    if (doc.getTextWidth(jl) > platz) jl = '';
    if (jl) doc.text(jl, (x1 + x2) / 2, jahrY + HEAD_JAHR_H / 2 + versalMm(b) / 2, { align: 'center' });
    setDraw(C.grid);
    doc.line(x1, jahrY, x1, monatY + HEAD_MONAT_H);
    gi = gj;
  }
  // Monatsbeschriftung: voll, sonst Anfangsbuchstabe, sonst nichts — EIN Modus
  // fuer die ganze Achse. Je Monat entschieden stand bei grosser Schrift nur
  // „J J J" da (die schmalen Initialen passten, die breiten nicht).
  doc.setFontSize(monatPt);
  const platzMonat = monatW - 0.6;
  const monatsModus = monate.every(m => doc.getTextWidth(MONATE[m.monat]) <= platzMonat) ? 'voll'
    : monate.every(m => doc.getTextWidth(MONATE[m.monat][0]) <= platzMonat) ? 'initiale' : 'keine';
  monate.forEach((m, i) => {
    const x = tx + i * monatW;
    const label = monatsModus === 'voll' ? MONATE[m.monat] : monatsModus === 'initiale' ? MONATE[m.monat][0] : '';
    if (label) doc.text(label, x + monatW / 2, monatY + HEAD_MONAT_H / 2 + versalMm(monatPt) / 2, { align: 'center' });
    setDraw(C.gridLight);
    doc.setLineWidth(0.1);
    doc.line(x, monatY, x, monatY + HEAD_MONAT_H);
  });
  setDraw(C.grid);
  doc.setLineWidth(0.2);
  doc.line(tx, jahrY, tx + TIME_W, jahrY);
  if (!hinweis) doc.line(tx, monatY, tx + TIME_W, monatY);
  doc.line(mx, my + headerH, mx + MATRIX_W, my + headerH);

  // ── Matrix-Zeilen ──────────────────────────────────────────────
  let y = my + headerH;

  const zelleText = (zelle: Zelle, x: number, w: number, yTop: number, opts?: { bold?: boolean; center?: boolean; rot?: boolean }) => {
    const kursiv = zelle.kursiv || [];
    if (!zelle.lines.length && !kursiv.length) return;
    doc.setFontSize(zelle.fs);
    setText(opts?.rot ? F.kritisch : C.text);
    const tx0 = opts?.center ? x + w / 2 : x + pad;
    const align = opts?.center ? 'center' : 'left';
    const y0 = yTop + pad + oberMm(zelle.fs);
    if (zelle.lines.length) {
      doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
      doc.text(zelle.lines, tx0, y0, { align });
    }
    // Abgesetzter kursiver Block (mitgeltende Dokumente) — Lage mit derselben
    // Rechnung wie die Zeilenhoehe (zellHoehe/kursivAbstand).
    if (kursiv.length) {
      doc.setFont('helvetica', 'italic');
      doc.text(kursiv, tx0, y0 + zelle.lines.length * zeilenMm(zelle.fs) + kursivAbstand(zelle), { align });
    }
  };

  sektionen.forEach(sek => {
    // Abschnitts-Band
    setFill(C.band);
    doc.rect(mx, y, MATRIX_W, SECTION_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(secPt);
    setText(C.weiss);
    doc.text(sek.titel, mx + 1.5, y + SECTION_H / 2 + versalMm(secPt) / 2);
    y += SECTION_H;

    sek.zeilen.forEach(z => {
      const h = z.hoehe;
      const s = z.schritt;

      // Zellrahmen — bei verbundener Zuständigkeit eine Zelle über drei Spalten
      setDraw(C.grid);
      doc.setLineWidth(0.15);
      colX.slice(0, -1).forEach((x, i) => {
        if (z.zustVerbunden && i > SP.erst) return;
        doc.rect(x, y, z.zustVerbunden && i === SP.erst ? CW.zustaendig * 3 : colW[i], h);
      });

      zelleText(z.arbeiten, colX[SP.arbeiten], colW[SP.arbeiten], y, { bold: !!s.kritisch, rot: !!s.kritisch });
      zelleText(z.massnahme, colX[SP.massnahme], colW[SP.massnahme], y);
      zelleText(z.vorschrift, colX[SP.vorschrift], colW[SP.vorschrift], y);
      zelleText(z.koord, colX[SP.koord], colW[SP.koord], y, { center: true });
      if (z.zustVerbunden) {
        zelleText(z.erst, colX[SP.erst], CW.zustaendig * 3, y, { center: true });
      } else {
        zelleText(z.erst, colX[SP.erst], colW[SP.erst], y, { center: true });
        zelleText(z.kontr, colX[SP.kontr], colW[SP.kontr], y, { center: true });
        zelleText(z.inst, colX[SP.inst], colW[SP.inst], y, { center: true });
      }

      // Zeitachse: Monatsgitter (ohne Zeitachse keine Monate, kein Gitter)
      monate.forEach((m, i) => {
        const x = tx + i * monatW;
        const istJahresgrenze = m.monat === 0;
        setDraw(istJahresgrenze ? C.grid : C.gridLight);
        doc.setLineWidth(istJahresgrenze ? 0.2 : 0.1);
        doc.line(x, y, x, y + h);
      });

      // Balken mit Datumslabels (wie Muster: Datum an den Enden)
      if (z.balken) {
        const { x1, x2, vonLabel, bisLabel, beideLabel, lage } = z.balken;
        const yb = y + h / 2 - balkenH / 2;
        const farbe = s.kritisch ? F.kritisch : F.balken;
        setFill(farbe);
        setDraw(farbe);
        doc.setLineWidth(0.1);
        doc.rect(tx + x1, yb, x2 - x1, balkenH, 'FD');

        doc.setFontSize(datumPt);
        doc.setFont('helvetica', 'normal');
        const yMitte = yb + balkenH / 2 + versalMm(datumPt) / 2;
        const setze = (text: string, pl?: DatumsPlatz) => {
          if (!pl) return;
          setText(pl.innen ? schriftAuf(farbe) : C.text);
          doc.text(text, tx + pl.x, pl.oben ? yb - datumHoch : yMitte, { align: pl.align });
        };
        setze(vonLabel, lage.von);
        setze(bisLabel, lage.bis);
        setze(beideLabel, lage.beide);
      }

      y += h;
    });
  });

  // Außenrahmen Matrix
  setDraw(C.text);
  doc.setLineWidth(0.4);
  doc.rect(mx, my, MATRIX_W, headerH + matrixInhaltH);
  const matrixUnten = my + headerH + matrixInhaltH;

  // ── Rechte Randspalte ──────────────────────────────────────────
  // Alle Masse sind fuer 6 pt Grundschrift entworfen und wachsen mit k.
  // Breiten bleiben (die Spalte ist fest), dafuer bricht mehr um — jede
  // Zeilenschaltung rechnet deshalb mit der tatsaechlichen Zeilenzahl.
  const sx = mx + MATRIX_W + GAP;
  let sy = randOU + TITLE_H;
  const innenW = SIDE_W - 6;

  const boxKopf = (titel: string, farbe: readonly number[], fontSize: number): number => {
    const hoehe = Math.max(6, fontSize * PT_MM * 1.75);
    setFill(farbe);
    doc.rect(sx, sy, SIDE_W, hoehe, 'F');
    doc.setFont('helvetica', 'bold');
    const fs = fitEinGroesse(titel, innenW, fontSize);
    setText(C.weiss);
    doc.text(titel, sx + SIDE_W / 2, sy + hoehe / 2 + versalMm(fs) / 2, { align: 'center' });
    return sy + hoehe;
  };
  // Groesste Schrift ≤ size, in der t einzeilig in w passt (Schrift muss vorher gesetzt sein).
  function fitEinGroesse(t: string, w: number, size: number): number {
    let fs = size;
    doc.setFontSize(fs);
    while (fs > 4 && doc.getTextWidth(t) > w) { fs = Math.max(4, fs - 0.2); doc.setFontSize(fs); }
    return fs;
  }

  // 1) NOTRUFPLAN
  {
    const boxTop = sy;
    let yy = boxKopf('NOTRUFPLAN', C.rot, p(13));
    const rufPt = p(8);
    yy += 1.2 * k + oberMm(rufPt);
    doc.setFontSize(rufPt);
    doc.setFont('helvetica', 'bold');
    setText(C.rot);
    const feuer = 'Feuerwehr / Rettungsdienst: 112';
    const polizei = 'Polizei: 110';
    doc.text(feuer, sx + 3, yy);
    if (doc.getTextWidth(feuer) + doc.getTextWidth(polizei) + 4 <= innenW) {
      doc.text(polizei, sx + SIDE_W - 3, yy, { align: 'right' });
    } else {
      yy += zeilenMm(rufPt);
      doc.text(polizei, sx + 3, yy);
    }
    yy += 4.5 * k;

    setText(C.text);
    doc.setFontSize(p(7));
    doc.text('Verhalten im Notfall', sx + SIDE_W / 2, yy, { align: 'center' });
    yy += 3.5 * k;
    const notfallPt = p(5.8);
    doc.setFontSize(notfallPt);
    const notfallSpalten = [
      ['Unfall melden:', '• Wer meldet?', '• Was ist passiert?', '• Wo ist es passiert?', '• Wie viele Verletzte?', '• Welche Verletzungen?'],
      ['Erste Hilfe:', '• Versorgung des Verletzten', '• Verletzte in sicheren Bereich bringen', '• Weisungen beachten', '• Verletzte nicht allein lassen'],
      ['Brandschutz:', '• Löschversuch unternehmen', '• Personenschutz vor Anlagenschutz'],
    ];
    const dreiW = innenW / 3;
    let maxYY = yy;
    notfallSpalten.forEach((spalte, i) => {
      let zy = yy;
      spalte.forEach((zeile, j) => {
        doc.setFont('helvetica', j === 0 ? 'bold' : 'normal');
        const ls = doc.splitTextToSize(zeile, dreiW - 1) as string[];
        doc.text(ls, sx + 3 + i * dreiW, zy);
        zy += ls.length * zeilenMm(notfallPt);
      });
      maxYY = Math.max(maxYY, zy);
    });
    yy = maxYY + 2 * k;

    // Kontakte
    const ni = (notruf?.inhalt || {}) as NotrufplanInhalt;
    const kontakte: { titel: string; k?: { name?: string; adresse?: string; telefon?: string; entfernung?: number } }[] = [
      { titel: 'Nächstes Krankenhaus', k: ni.krankenhaus },
      { titel: 'Durchgangsarzt', k: ni.durchgangsarzt },
      { titel: 'Augenarzt', k: ni.augenarzt },
      { titel: 'Apotheke', k: ni.apotheke },
    ];
    const zweiW = innenW / 2;
    const kontaktPt = p(5.8);
    for (let i = 0; i < kontakte.length; i += 2) {
      let maxZ = yy;
      [kontakte[i], kontakte[i + 1]].filter(Boolean).forEach((eintrag, j) => {
        const x0 = sx + 3 + j * zweiW;
        let zy = yy;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(p(6));
        doc.text(eintrag.titel, x0, zy); zy += 2.6 * k;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(kontaktPt);
        const zeilen = [
          eintrag.k?.name,
          eintrag.k?.adresse,
          eintrag.k?.telefon ? `Tel.: ${eintrag.k.telefon}` : '',
          eintrag.k?.entfernung != null ? `ca. ${eintrag.k.entfernung} km` : '',
        ].filter(Boolean) as string[];
        // Keine Stelle im Notrufplan: benannt, nicht „—" — der Strich saehe
        // aus, als gaebe es kein Krankenhaus zu nennen. Dasselbe Wort wie in
        // der Rettungskette des A+S-Plans (`notfallangaben.ts`).
        (zeilen.length ? zeilen : ['nicht hinterlegt']).forEach(z => {
          const ls = doc.splitTextToSize(z, zweiW - 2) as string[];
          doc.text(ls, x0, zy);
          zy += ls.length * zeilenMm(kontaktPt);
        });
        maxZ = Math.max(maxZ, zy);
      });
      yy = maxZ + 2 * k;
    }

    // Kartenausschnitt mit Markern (P = Projekt, 1 = Krankenhaus, 2 = Augenarzt, 3 = Apotheke)
    if (ni.kartenBild) {
      const imgW = Math.min(innenW, KARTE_MAX_W);
      const imgH = imgW * 360 / 640;
      try {
        const format = ni.kartenBild.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(ni.kartenBild, format, sx + (SIDE_W - imgW) / 2, yy, imgW, imgH);
        yy += imgH + 1.5 * k + oberMm(p(5.2));
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(p(5.2));
        setText(C.text);
        // Legende mit farbigen Punkten — der Durchgangsarzt fehlte hier, weil er
        // auch auf der Karte keinen Marker hatte. Reihenfolge und Farben stammen
        // aus notrufAuto.KARTEN_MARKER; wer eine aendert, muss beide anfassen.
        const legende: { farbe: readonly number[]; text: string }[] = [
          { farbe: C.rot, text: 'P Projekt' },
          { farbe: C.blau, text: 'K Krankenhaus' },
          { farbe: C.violett, text: 'D Durchgangsarzt' },
          { farbe: C.gruen, text: 'A Augenarzt' },
          { farbe: C.gelb, text: 'Ap Apotheke' },
        ];
        const r = k;
        let lx = sx + 3;
        let ly = yy;
        legende.forEach(e => {
          const breite = doc.getTextWidth(e.text) + 5 * k;
          if (lx + breite > sx + SIDE_W - 3) { lx = sx + 3; ly += 3 * k; }
          setFill(e.farbe);
          doc.circle(lx + r, ly - 0.7 * k, r, 'F');
          setText(C.text);
          doc.text(e.text, lx + 3 * k, ly);
          lx += breite;
        });
        yy = ly + 3 * k;
        doc.text('© OpenStreetMap-Mitwirkende', sx + 3, yy);
        yy += 3 * k;
        // Ohne eigene Baumassnahme-Adresse ist die Karte auf eine Ersatzadresse
        // zentriert. Das MUSS auf dem Blatt stehen — sonst liest sich der Punkt
        // „P" als die Baustelle, die er nicht ist.
        if (ni.ersatzAdresse && !project.baumassnahme_adresse?.trim()) {
          doc.setFont('helvetica', 'bold');
          setText(C.rot);
          const hw = doc.splitTextToSize(
            `Achtung: Karte und Entfernungen beziehen sich auf die Ersatzadresse ${ni.ersatzAdresse} — nicht auf die Baustelle.`,
            innenW,
          ) as string[];
          doc.text(hw, sx + 3, yy);
          yy += hw.length * zeilenMm(p(5.2)) + k;
          doc.setFont('helvetica', 'normal');
          setText(C.text);
        }
      } catch (e) {
        console.warn('Kartenbild konnte nicht eingebettet werden', e);
      }
    }

    // Ersthelfer
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(p(6.5));
    doc.text('Ersthelfer auf der Baustelle:', sx + 3, yy);
    yy += 3 * k;
    const ehPt = p(5.8);
    doc.setFontSize(ehPt);
    doc.text('Name', sx + 3, yy);
    doc.text('Firma', sx + 3 + zweiW, yy);
    setDraw(C.grid); doc.setLineWidth(0.15);
    doc.line(sx + 2, yy + 0.8 * k, sx + SIDE_W - 2, yy + 0.8 * k);
    yy += 3 * k;
    doc.setFont('helvetica', 'normal');
    const eh = ni.ersthelfer || [];
    const ehZeilen = Math.max(eh.length, 3);
    for (let i = 0; i < ehZeilen; i++) {
      doc.text(fitEin(eh[i]?.name || '', zweiW - 2, ehPt), sx + 3, yy);
      doc.text(fitEin(eh[i]?.firma || '', zweiW - 2, ehPt), sx + 3 + zweiW, yy);
      doc.setFontSize(ehPt);
      doc.line(sx + 2, yy + 0.8 * k, sx + SIDE_W - 2, yy + 0.8 * k);
      yy += 3.2 * k;
    }
    yy += k;

    setDraw(C.rot);
    doc.setLineWidth(0.5);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy + 3;
  }

  // 2) LAST MINUTE RISK ASSESSMENT
  {
    const boxTop = sy;
    const lmraPt = p(5.8);
    let yy = boxKopf('Last Minute Risk Assessment', C.blau, p(10));
    yy += 1 * k + oberMm(lmraPt);
    doc.setFontSize(lmraPt);
    setText(C.text);
    lmraZeilen.forEach(zeile => {
      if (!zeile.trim()) { yy += 1.5 * k; return; }
      const istTitel = zeile.includes(':') && zeile.indexOf(':') < 25;
      doc.setFont('helvetica', istTitel ? 'bold' : 'normal');
      const ls = doc.splitTextToSize(zeile, innenW) as string[];
      doc.text(ls, sx + 3, yy);
      yy += ls.length * zeilenMm(lmraPt) + 0.6 * k;
    });
    yy += k;
    setDraw(C.blau);
    doc.setLineWidth(0.5);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy + 3;
  }

  // 3) Piktogramme (Gebots-/Warn-/Verbotszeichen, vereinfacht gezeichnet)
  {
    const boxTop = sy;
    let yy = boxKopf('Gebots- und Warnzeichen', F.kopf, p(8));
    yy += 3;
    const proZeile = 4;
    const zellW = innenW / proZeile;
    const iconR = Math.min(6 * k, zellW / 2 - 2);
    const labelPt = p(4.6);
    // Je Reihe: Zeichen + die laengste Beschriftung dieser Reihe
    const defs = piktoKeys.map(key => ({ key, def: aufloesePikto(key) }));
    for (let start = 0; start < defs.length; start += proZeile) {
      const reihe = defs.slice(start, start + proZeile);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(labelPt);
      const labels = reihe.map(r => (r.def ? doc.splitTextToSize(r.def.label, zellW - 1) as string[] : []));
      const maxLabel = Math.max(1, ...labels.map(l => l.length));
      reihe.forEach(({ key, def }, i) => {
        if (!def) return;
        const cx = sx + 3 + i * zellW + zellW / 2;
        const cy = yy + iconR;
        const png = piktoBilder[key];
        if (png) {
          // echtes ISO-7010-Zeichen (als PNG gerastert)
          try { doc.addImage(png, 'PNG', cx - iconR, cy - iconR, iconR * 2, iconR * 2); }
          catch { /* falls addImage scheitert, einfach leer lassen */ }
        } else {
          // Fallback: einfache Form + Kürzel
          if (def.typ === 'gebot') {
            setFill(C.blau); doc.circle(cx, cy, iconR, 'F'); setText(C.weiss);
          } else if (def.typ === 'warnung') {
            setFill(C.gelb); doc.triangle(cx, cy - iconR, cx - iconR, cy + iconR, cx + iconR, cy + iconR, 'F'); setText(C.text);
          } else if (def.typ === 'rettung') {
            setFill(C.gruen); doc.rect(cx - iconR, cy - iconR, iconR * 2, iconR * 2, 'F'); setText(C.weiss);
          } else if (def.typ === 'brandschutz') {
            setFill(C.rot); doc.rect(cx - iconR, cy - iconR, iconR * 2, iconR * 2, 'F'); setText(C.weiss);
          } else {
            setFill(C.rot); doc.circle(cx, cy, iconR, 'F'); setText(C.weiss);
          }
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(p(8));
          doc.text(def.symbol, cx, cy + (def.typ === 'warnung' ? iconR / 2 + k : 1.2 * k), { align: 'center' });
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(labelPt);
        setText(C.text);
        doc.text(labels[i], cx, cy + iconR + 1 * k + oberMm(labelPt), { align: 'center' });
      });
      yy += iconR * 2 + 1 * k + maxLabel * zeilenMm(labelPt) + 2.5 * k;
    }
    yy += 2;
    setDraw(F.kopf);
    doc.setLineWidth(0.4);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy + 3;
  }

  // 3b) Einweisung in den SiGe-Plan — QR-Code der Online-Einweisung.
  // Nur mit aktivem Link; ohne ihn entsteht hier NICHTS (kein Platzhalter),
  // damit ein Plan ohne Einweisung genauso aussieht wie bisher.
  if (einweisungQr) {
    const boxTop = sy;
    let yy = boxKopf('Einweisung in den SiGe-Plan', F.kopf, p(8));
    yy += 3;
    const qrMm = Math.min(EINWEISUNG_QR_MM, innenW);
    try {
      doc.addImage(einweisungQr.dataUrl, 'PNG', sx + 3, yy, qrMm, qrMm);
    } catch (e) {
      console.warn('QR-Code der Einweisung konnte nicht eingebettet werden', e);
    }
    // Rechts neben dem QR: die Handlungsanweisung und die Nummer der Einweisung.
    const textX = sx + 3 + qrMm + 3;
    const textW = sx + SIDE_W - 3 - textX;
    const qrPt = p(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(qrPt);
    setText(C.text);
    const hinweis = doc.splitTextToSize(
      'QR-Code scannen, Einweisung lesen, Name eintragen und unterschreiben.',
      textW,
    ) as string[];
    doc.text(hinweis, textX, yy + oberMm(qrPt));
    doc.setFont('helvetica', 'bold');
    const nrY = yy + oberMm(qrPt) + hinweis.length * zeilenMm(qrPt) + 1.5 * k;
    doc.text(fitEin(`Einweisung Nr. ${einweisungQr.nummer}`, textW, qrPt), textX, nrY);
    yy += Math.max(qrMm, nrY - yy + 1) + 3;
    // Der Link klein darunter — fuer alle, die den Code nicht scannen koennen.
    const linkPt = p(5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(linkPt);
    const linkZeilen = doc.splitTextToSize(einweisungQr.url, innenW) as string[];
    doc.text(linkZeilen, sx + 3, yy);
    yy += linkZeilen.length * zeilenMm(linkPt) + 1.5 * k;
    setDraw(F.kopf);
    doc.setLineWidth(0.4);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy + 3;
  }

  // 4) Legende — Kuerzelspalte so breit wie das laengste Kuerzel (hoechstens
  // gut ein Drittel), die Bedeutung bricht im Rest um. Die Eintraege kommen
  // aus legendeFuerPlan (gespeichert bzw. Muster, bereinigt, plus Kurzformen
  // der Dokumente).
  //
  // Darunter der Grundsatz-Satz fuer die allgemeinen Arbeitgeberunterlagen
  // (Art C). Er steht einmal auf dem Blatt statt je Zeile — sonst bekaeme
  // jede Zeile zwei, drei Dokumente mehr. Platz in der Legendenbox, weil sie
  // die einzige Stelle ist, die es auf JEDEM Blatt gibt (ohne Block die
  // Mustervorlage) und die Messlauf und Reinzeichnung ohnehin gleich setzen;
  // eine eigene Box oder ein Fussabsatz unter der Matrix haette einen zweiten
  // Pfad in die Hoehenrechnung gebracht. Auch ohne ein einziges Dokument
  // gedruckt: Art C gilt immer.
  {
    const boxTop = sy;
    let yy = boxKopf('Legende', F.kopf, p(8));
    const legPt = p(5.4);
    yy += 1.2 * k + oberMm(legPt);
    doc.setFontSize(legPt);
    setText(C.text);
    doc.setFont('helvetica', 'bold');
    const kuerzelW = Math.min(innenW * 0.36, Math.max(10, ...legendeEintraege.map(le => doc.getTextWidth(le.kuerzel || ''))) + 3);
    legendeEintraege.forEach(le => {
      doc.setFont('helvetica', 'bold');
      const kl = doc.splitTextToSize(le.kuerzel || '', kuerzelW - 1) as string[];
      doc.text(kl, sx + 3, yy);
      doc.setFont('helvetica', 'normal');
      const ls = doc.splitTextToSize(le.bedeutung || '', innenW - kuerzelW) as string[];
      doc.text(ls, sx + 3 + kuerzelW, yy);
      yy += Math.max(kl.length, ls.length, 1) * zeilenMm(legPt) + 0.4 * k;
    });
    // `yy` ist hier die Grundlinie eines naechsten Eintrags; der Satz setzt
    // mit etwas Luft darunter ueber die ganze Innenbreite an.
    doc.setFont('helvetica', 'normal');
    if (legendeEintraege.length) yy += 1.2 * k;
    const grundsatz = doc.splitTextToSize(GRUNDSATZ_ART_C, innenW) as string[];
    doc.text(grundsatz, sx + 3, yy);
    yy += grundsatz.length * zeilenMm(legPt) + 0.4 * k;
    yy += k;
    setDraw(F.kopf);
    doc.setLineWidth(0.4);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy + 3;
  }

  // Koordination: die im Plankopf gepflegten Verantwortlichen, sonst der SiGeKo
  // aus den Stammdaten. Plankopf und Fusszeile lesen dieselbe Liste, damit
  // unten nie ein anderer Name steht als rechts.
  const koordination: PlanVerantwortlicher[] = (() => {
    const gepflegt = (project.darstellung?.verantwortliche || []).filter(v => v.name?.trim());
    if (gepflegt.length) return gepflegt;
    return project.sigeko_name
      ? [{ name: project.sigeko_name, rolle: 'SiGeKo', telefon: project.sigeko_mobil, email: project.sigeko_email }]
      : [];
  })();

  // 5) Plankopf (unten in der Randspalte, wie Muster unten rechts)
  {
    const boxTop = sy;
    let yy = sy;
    const zeileBox = (hoehe: number, fill = false) => {
      if (fill) { setFill(C.hellgrau); doc.rect(sx, yy, SIDE_W, hoehe, 'F'); }
      setDraw(C.text); doc.setLineWidth(0.25);
      doc.rect(sx, yy, SIDE_W, hoehe);
      yy += hoehe;
    };
    const grau = [110, 110, 110] as const;

    // Aufsteller — Logo links, Firmendaten rechts daneben.
    const auf = project.aufsteller || {};
    {
      const yTop = yy;
      // Logo zuerst vermessen: es bestimmt die Hoehe der Box mit.
      let logoBox: { width: number; height: number } | null = null;
      if (auf.logo_url) {
        try {
          logoBox = fitLogoBox(doc.getImageProperties(auf.logo_url), Math.min(24 * k, SIDE_W * 0.3), 11 * k);
        } catch (e) {
          console.warn('Aufsteller-Logo konnte nicht gelesen werden', e);
        }
      }
      const h0 = Math.max(14 * k, (logoBox?.height || 0) + 5);
      zeileBox(h0, true);
      const textX = sx + 3 + (logoBox ? logoBox.width + 3 : 0);
      const textW = (sx + SIDE_W - 3) - textX;
      if (logoBox) {
        try {
          doc.addImage(auf.logo_url!, imgFmt(auf.logo_url!), sx + 3, yTop + (h0 - logoBox.height) / 2, logoBox.width, logoBox.height);
        } catch (e) {
          console.warn('Aufsteller-Logo konnte nicht eingebettet werden', e);
        }
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(p(9));
      setText(F.kopf);
      doc.text(fit(auf.firma || 'SiGeKo-Büro', textW), textX, yTop + 5.5 * k);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(p(5.8));
      setText(C.text);
      const aufZeilen = [
        [auf.adresse || '', auf.telefon ? `Tel: ${auf.telefon}` : ''].filter(Boolean).join('  •  '),
        [auf.email || '', auf.web || ''].filter(Boolean).join('  •  '),
      ].filter(Boolean);
      aufZeilen.forEach((z, i) => doc.text(fit(z, textW), textX, yTop + 9 * k + i * 3 * k));
    }

    // Koordination — Koordinator und seine Vertretung stehen namentlich auf dem
    // Plan. Vorher trug der Plankopf nur den SiGeKo-Namen in der Planinhalt-Box;
    // wer im Notfall wen vertritt, stand nirgends.
    {
      const liste = koordination;
      if (liste.length) {
        const yTop = yy;
        const h0 = (4.5 + liste.length * 5.8) * k;
        zeileBox(h0);
        doc.setFontSize(p(5.2));
        setText(grau);
        doc.text('Koordination / Vertretung', sx + 3, yTop + 3 * k);
        setText(C.text);
        liste.forEach((v, i) => {
          const zy = yTop + (6.6 + i * 5.8) * k;
          doc.setFont('helvetica', 'bold');
          const rolle = v.rolle?.trim() || (i === 0 ? 'SiGeKo' : 'Vertretung');
          doc.text(fitEin(`${rolle}: ${v.name}`, innenW, p(6.2)), sx + 3, zy);
          doc.setFont('helvetica', 'normal');
          const kontakt = [v.telefon ? `Tel: ${v.telefon}` : '', v.email || ''].filter(Boolean).join('  •  ');
          if (kontakt) doc.text(fitEin(kontakt, innenW, p(5.4)), sx + 3, zy + 2.9 * k);
        });
      }
    }
    // bearbeitet / geprüft
    {
      const h0 = 11 * k;
      const yTop = yy;
      zeileBox(h0);
      doc.setFontSize(p(5.4));
      doc.setFont('helvetica', 'normal');
      const c1 = sx + SIDE_W * 0.42, c2 = sx + SIDE_W * 0.66;
      doc.text('Datum', c1 + 2, yTop + 3 * k);
      doc.text('Name', c2 + 2, yTop + 3 * k);
      const nameW = (sx + SIDE_W - 2) - (c2 + 2);
      doc.setFontSize(p(6));
      doc.text('bearbeitet', sx + 3, yTop + 6.5 * k);
      doc.text(fmtDatum(project.bearbeitet_datum || project.plan_datum), c1 + 2, yTop + 6.5 * k);
      doc.text(fitEin(project.bearbeitet_name || project.sigeko_name || '', nameW, p(6)), c2 + 2, yTop + 6.5 * k);
      doc.setFontSize(p(6));
      doc.text('geprüft', sx + 3, yTop + 9.8 * k);
      doc.text(fmtDatum(project.geprueft_datum), c1 + 2, yTop + 9.8 * k);
      doc.text(fitEin(project.geprueft_name || '', nameW, p(6)), c2 + 2, yTop + 9.8 * k);
      setDraw(C.grid); doc.setLineWidth(0.15);
      doc.line(sx, yTop + 4 * k, sx + SIDE_W, yTop + 4 * k);
      doc.line(sx, yTop + 7.5 * k, sx + SIDE_W, yTop + 7.5 * k);
      doc.line(c1, yTop, c1, yTop + h0);
      doc.line(c2, yTop, c2, yTop + h0);
    }
    // Bauherr
    {
      const h0 = 10 * k;
      const yTop = yy;
      zeileBox(h0);
      doc.setFontSize(p(5.2));
      setText(grau);
      doc.text('Bauherr', sx + 3, yTop + 3 * k);
      doc.setFontSize(p(6.5));
      doc.setFont('helvetica', 'bold');
      setText(C.text);
      doc.text(fit(project.bauherr_name || '', innenW), sx + 3, yTop + 6.2 * k);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(p(5.4));
      doc.text(fit(project.bauherr_adresse || '', innenW), sx + 3, yTop + 8.8 * k);
    }
    // Baumaßnahme — Name darf zweizeilig laufen (lange Bauvorhaben-Bezeichnungen),
    // Box daher 3 mm höher; die folgenden Boxen rücken über yy automatisch nach.
    {
      const h0 = 13 * k;
      const yTop = yy;
      zeileBox(h0);
      doc.setFontSize(p(5.2));
      setText(grau);
      doc.text('Baumaßnahme', sx + 3, yTop + 3 * k);
      doc.setFontSize(p(6.2));
      doc.setFont('helvetica', 'bold');
      setText(C.text);
      doc.text(fit(project.baumassnahme_name || '', innenW, 2), sx + 3, yTop + 6.2 * k, { lineHeightFactor: 1.15 });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(p(5.4));
      doc.text(fit(project.baumassnahme_adresse || '', innenW), sx + 3, yTop + 11.6 * k);
    }
    // Planinhalt + Plan-Nr/Datum + SiGeKo
    {
      const h0 = 17 * k;
      const yTop = yy;
      zeileBox(h0);
      const cSplit = sx + SIDE_W * 0.58;
      setDraw(C.grid); doc.setLineWidth(0.15);
      doc.line(cSplit, yTop, cSplit, yTop + h0);
      doc.setFontSize(p(5.2));
      setText(grau);
      doc.text('Planinhalt', sx + 3, yTop + 3 * k);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(p(7));
      setText(F.kopf);
      const pl = doc.splitTextToSize(project.plan_inhalt || 'Sicherheits- und Gesundheitsschutzplan', SIDE_W * 0.58 - 6) as string[];
      doc.text(pl.slice(0, 3), sx + 3, yTop + 7 * k, { lineHeightFactor: 1.15 });
      doc.setFont('helvetica', 'normal');
      setText(C.text);
      // rechte Hälfte: nur cSplit+2 … sx+SIDE_W-2 verfügbar (≈ SIDE_W*0.42−4)
      const rW = (sx + SIDE_W - 2) - (cSplit + 2);
      const rPt = p(5.6);
      // Fehlende Plan-Nr. benannt statt „—": der Strich laese sich als „es
      // gibt keine" — der Plan traegt aber im Regelfall eine Nummer, sie ist
      // nur noch nicht eingetragen.
      doc.text(fitEin(`Plan-Nr.: ${project.plan_nr || NICHT_ANGEGEBEN}`, rW, rPt), cSplit + 2, yTop + 3.5 * k);
      doc.text(fitEin(`Datum: ${fmtDatum(project.plan_datum)}`, rW, rPt), cSplit + 2, yTop + 6.5 * k);
      if (project.sigeko_name) {
        doc.text(fitEin(`SiGeKo: ${project.sigeko_name}`, rW, rPt), cSplit + 2, yTop + 10 * k);
        if (project.sigeko_mobil) doc.text(fitEin(`Mobil: ${project.sigeko_mobil}`, rW, rPt), cSplit + 2, yTop + 12.6 * k);
        if (project.sigeko_email) doc.text(fitEin(project.sigeko_email, rW, rPt), cSplit + 2, yTop + 15.2 * k);
      }
    }
    setDraw(C.text);
    doc.setLineWidth(0.5);
    doc.rect(sx, boxTop, SIDE_W, yy - boxTop);
    sy = yy;   // Unterkante der Randspalte — bestimmt mit der Matrix die Blatthoehe
  }

  // ── Fusszeile ──────────────────────────────────────────────────
  // Projekt-Nr. links, Projektbezeichnung mittig, Koordinator rechts — noch
  // einmal ueber die ganze Blattbreite. Der Plankopf sitzt unten rechts in der
  // Randspalte und ist auf dem gerollten oder gefalteten Plot oft verdeckt;
  // der schmale Streifen am unteren Rand sagt auf einen Blick, zu welchem
  // Projekt das Blatt gehoert. Jedes Feld bleibt in seinem Drittel: lange
  // Bezeichnungen werden kleiner gesetzt oder gekuerzt, nie ueberlappt.
  //
  // Grenzfall MAX_H: ein Plan, der die 5-m-Kappung erreicht, ist ohnehin
  // abgeschnitten; die Fusszeile deckt dann die letzten mm der Matrix ab.
  // Bewusst hingenommen — das Blatt soll auch dann noch sagen, wozu es gehoert.
  {
    const fussH = fussHoeheMm(b);
    const fs = fussPt(b);
    const fy = H - randOU - fussH;
    const fw = W - 2 * M;
    const pad2 = 2;
    const drittel = fw / 3 - 2 * pad2;
    const ty = fy + fussH / 2;
    setFill(C.hellgrau);
    doc.rect(M, fy, fw, fussH, 'F');
    setDraw(C.grid); doc.setLineWidth(0.2);
    doc.rect(M, fy, fw, fussH);

    const projektNummer = (fusszeile?.projektNummer || '').trim();
    const projektName = (fusszeile?.projektName || project.baumassnahme_name || '').trim();
    const koordinator = koordination.find(v => /sigeko|koordinator/i.test(v.rolle || '')) ?? koordination[0];

    doc.setFont('helvetica', 'normal');
    setText(C.text);
    if (projektNummer) {
      doc.setFont('helvetica', 'bold');
      doc.text(fitEin(`Projekt-Nr.: ${projektNummer}`, drittel, fs), M + pad2, ty, { baseline: 'middle' });
      doc.setFont('helvetica', 'normal');
    }
    if (projektName) {
      doc.text(fitEin(projektName, drittel, fs), M + fw / 2, ty, { align: 'center', baseline: 'middle' });
    }
    if (koordinator?.name) {
      doc.text(fitEin(`Koordinator: ${koordinator.name}`, drittel, fs), M + fw - pad2, ty, { align: 'right', baseline: 'middle' });
    }
  }

  return { doc, unten: Math.max(matrixUnten, sy) };
};

/**
 * Wie generateSigePlanPdf, zusaetzlich mit dem Blattmass — die Vorschau
 * zeigt es neben dem Schriftregler an, damit man sieht, was ein Punkt mehr
 * an Rollenlaenge kostet.
 *
 * Der Generator kennt keine Firmenvorgaben. Breite und Schrift, die der Plan
 * nicht selbst traegt, setzt der Aufrufer vorher mit `projektFuerDruck` ein
 * (PlanVorschau, usePdfExport) — nur fuer den Druck, gespeichert wird davon
 * nichts.
 */
export const erzeugeSigePlan = (
  project: SigeProject,
  arbeitsschritte: Arbeitsschritt[],
  gefaehrdungen: Record<string, Gefaehrdung[]>,
  massnahmen: Record<string, Schutzmassnahme[]>,
  bloecke: SigeBlock[],
  piktoBilder: Record<string, string> = {},
  einweisungQr?: EinweisungQrQuelle | null,
  fusszeile?: SigePlanFusszeile | null
): { blob: Blob; breiteMm: number; hoeheMm: number; gekappt: boolean } => {
  const eingabe: PlanEingabe = { project, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, piktoBilder, einweisungQr, fusszeile };
  // Zwei Laeufe: Messlauf auf dem uebergrossen Blatt liefert die tiefste
  // Inhaltskante, die Reinzeichnung bekommt genau diese Hoehe plus Luft,
  // Fusszeile und Rand. So endet das Blatt dort, wo der Inhalt endet.
  const { unten } = zeichnePlan(eingabe, null);
  const benoetigt = unten + LUFT_UNTEN + fussHoeheMm(planSchriftPt(project.darstellung)) + randObenUntenMm(project.darstellung);
  const H = Math.min(MAX_H, benoetigt);
  const { doc } = zeichnePlan(eingabe, H);
  return {
    blob: doc.output('blob'),
    breiteMm: doc.internal.pageSize.getWidth(),
    hoeheMm: doc.internal.pageSize.getHeight(),
    // Laenger als die Rolle: unten fehlt Inhalt — die Vorschau sagt es.
    gekappt: benoetigt > MAX_H,
  };
};

export const generateSigePlanPdf = (
  project: SigeProject,
  arbeitsschritte: Arbeitsschritt[],
  gefaehrdungen: Record<string, Gefaehrdung[]>,
  massnahmen: Record<string, Schutzmassnahme[]>,
  bloecke: SigeBlock[],
  piktoBilder: Record<string, string> = {},
  einweisungQr?: EinweisungQrQuelle | null,
  fusszeile?: SigePlanFusszeile | null
): Blob => erzeugeSigePlan(project, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, piktoBilder, einweisungQr, fusszeile).blob;
